import { useState, type FormEvent } from 'react';
import { Button, FormField, InlineNotification, TextInput } from '@dpem/design-system';
import { PasswordSchema } from '@dpem/contracts';
import { Link, Navigate, useSearchParams } from 'react-router-dom';

import { useAuth } from '@/app/providers/AuthProvider';
import { presentError } from '@/lib/api/errors';
import {
  DEFAULT_AUTHENTICATED_PATH,
  PATHS,
  REDIRECT_PARAM,
  safeRedirectTarget,
} from '@/router/paths';
import { AuthLayout } from './AuthLayout';
import styles from './AuthLayout.module.css';

/**
 * Register.
 *
 * The password rule is read from `PasswordSchema` rather than restated, so the
 * client and the server cannot disagree about the minimum — a form that accepts
 * what the API rejects is worse than no validation at all.
 *
 * The confirmation field is checked here and nowhere else: the API has no notion of
 * it, because a typo is a client-side concern and sending the password twice would
 * only widen what is on the wire.
 *
 * The first account on a fresh install becomes ADMIN; every later self-registration
 * lands on VIEWER. That is stated on the form because a new author who registers
 * expecting to author needs to know they must be granted the role.
 */

/** Read off the shared schema so the two cannot drift. */
const MIN_PASSWORD_LENGTH = PasswordSchema.minLength ?? 12;

export function RegisterPage() {
  const { status, register, pending } = useAuth();
  const [searchParams] = useSearchParams();

  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [invitationCode, setInvitationCode] = useState('');
  const [failure, setFailure] = useState<string | null>(null);
  const [touched, setTouched] = useState(false);

  const target = safeRedirectTarget(searchParams.get(REDIRECT_PARAM));

  if (status === 'restoring') return null;
  if (status === 'authenticated') return <Navigate to={target} replace />;

  const tooShort = password.length > 0 && password.length < MIN_PASSWORD_LENGTH;
  const mismatched = confirmation.length > 0 && confirmation !== password;
  const submittable =
    displayName.trim() !== '' &&
    email.trim() !== '' &&
    password.length >= MIN_PASSWORD_LENGTH &&
    confirmation === password;

  const onSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    setTouched(true);
    if (!submittable) return;

    setFailure(null);
    void register({
      email,
      password,
      displayName: displayName.trim(),
      // Omitted rather than sent empty: the schema treats it as optional, and an
      // empty string is a value the server would have to interpret.
      ...(invitationCode.trim() === '' ? {} : { invitationCode: invitationCode.trim() }),
    }).catch((error: unknown) => {
      const presented = presentError(error);
      setFailure(
        presented.code === 'ALREADY_EXISTS'
          ? 'An account already exists for that email address. Sign in instead.'
          : presented.title,
      );
      setPassword('');
      setConfirmation('');
    });
  };

  const loginTo =
    target === DEFAULT_AUTHENTICATED_PATH
      ? PATHS.login
      : `${PATHS.login}?${REDIRECT_PARAM}=${encodeURIComponent(target)}`;

  return (
    <AuthLayout
      title="Create an account"
      subtitle="The first account on a new installation becomes the administrator. Later accounts start as viewers until a role is granted."
    >
      {failure !== null && (
        <InlineNotification kind="error" title="Registration failed" onClose={() => setFailure(null)}>
          {failure}
        </InlineNotification>
      )}

      <form className={styles.fields} onSubmit={onSubmit} noValidate>
        <FormField
          label="Full name"
          hint="Shown on methods you author and on workshop records."
          error={touched && displayName.trim() === '' ? 'Enter your name.' : undefined}
          required
        >
          <TextInput
            name="displayName"
            autoComplete="name"
            autoFocus
            required
            value={displayName}
            onChange={(event) => setDisplayName(event.target.value)}
          />
        </FormField>

        <FormField
          label="Email address"
          error={touched && email.trim() === '' ? 'Enter your email address.' : undefined}
          required
        >
          <TextInput
            type="email"
            name="email"
            autoComplete="email"
            required
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
        </FormField>

        <FormField
          label="Password"
          hint={`At least ${String(MIN_PASSWORD_LENGTH)} characters. Length beats complexity — a passphrase is fine.`}
          error={tooShort ? `Use at least ${String(MIN_PASSWORD_LENGTH)} characters.` : undefined}
          required
        >
          <TextInput
            type="password"
            name="password"
            autoComplete="new-password"
            required
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
        </FormField>

        <FormField
          label="Confirm password"
          error={mismatched ? 'The two passwords do not match.' : undefined}
          required
        >
          <TextInput
            type="password"
            name="passwordConfirmation"
            autoComplete="new-password"
            required
            value={confirmation}
            onChange={(event) => setConfirmation(event.target.value)}
          />
        </FormField>

        <FormField
          label="Invitation code"
          hint="Optional. Supplied by an administrator to grant a role on creation."
        >
          <TextInput
            name="invitationCode"
            autoComplete="off"
            value={invitationCode}
            onChange={(event) => setInvitationCode(event.target.value)}
          />
        </FormField>

        <Button type="submit" loading={pending} fullWidth>
          Create account
        </Button>
      </form>

      <p className={styles.footer}>
        Already registered?{' '}
        <Link to={loginTo} className={styles.link}>
          Sign in
        </Link>
      </p>
    </AuthLayout>
  );
}
